/**
 * Color utility functions for consistent color handling across the application
 */

/**
 * Darkens a color name by creating a temporary DOM element and applying color transformations
 * @param {string} colorName - CSS color name (e.g., 'blue', 'red', 'green')
 * @returns {string} Darkened color in RGB format or original color if processing fails
 */
export const darkenColor = (colorName) => {
  try {
    // Handle edge cases
    if (!colorName || typeof colorName !== "string") {
      return "#000000"; // Default to black
    }

    // Create a temporary element to get the computed color
    const tempDiv = document.createElement("div");
    tempDiv.style.color = colorName;
    document.body.appendChild(tempDiv);

    const computedColor = window.getComputedStyle(tempDiv).color;
    document.body.removeChild(tempDiv);

    // Parse RGB values from the computed color
    const rgbMatch = computedColor.match(/rgb\((\d+), (\d+), (\d+)\)/);
    if (!rgbMatch) {
      // If RGB parsing fails, return original color
      return colorName;
    }

    const [, r, g, b] = rgbMatch.map(Number);

    // Apply darkening transformation (60% of original brightness)
    const darkerR = Math.round(r * 0.6);
    const darkerG = Math.round(g * 0.6);
    const darkerB = Math.round(b * 0.6);

    // Special handling for very light colors
    if (r > 240 && g > 240 && b > 240) {
      return "#ffffff"; // Keep white as white
    }

    return `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
  } catch (error) {
    console.warn("Error darkening color:", colorName, error);
    // Return original color as fallback
    return colorName;
  }
};

/**
 * Gets a darkened CSS color for lens color display
 * Used for consistent color representation across the application
 * @param {string|object} colorData - Color name string or color object with name/cssColor properties
 * @returns {string} Darkened CSS color value
 */
export const getLensDisplayColor = (colorData) => {
  if (!colorData) return "#000000";

  // Handle color object
  if (typeof colorData === "object") {
    if (colorData.cssColor) {
      return colorData.cssColor; // Already processed/darkened
    }
    if (colorData.name) {
      return darkenColor(colorData.name.toLowerCase());
    }
  }

  // Handle color string
  if (typeof colorData === "string") {
    return darkenColor(colorData.toLowerCase());
  }

  return "#000000"; // Fallback
};
